import { Formik, FormikConfig, FormikHelpers, FormikValues } from "formik";
import React, { useEffect, useState } from "react";
import AppMessage from "src/commun/composants/AppMessage/AppMessage";
import AppSpinner from "src/commun/composants/AppSpinner/AppSpinner";
import AppCol from "src/commun/composants/grille/AppCol";
import AppRow from "src/commun/composants/grille/AppRow";
import useIsMounted from "src/commun/hooks/useIsMounted";
import useTranslateErrorMessage from "src/commun/hooks/useTranslateErrorMessage";
import { GenericApiResponse } from "src/app/api/AppApiClient";
import styles from "src/commun/composants/formulaire/formik/AppSpinnerFormik.scss";
import { AxiosError } from "axios";


export type AppFormikConfig<AppFormikValues extends FormikValues, Result = GenericApiResponse<any>> = Omit<
  FormikConfig<AppFormikValues>,
  "onSubmit"
> & {
  displayErrorsOnTop?: boolean;
  submitFormAPI?: (values: AppFormikValues) => Promise<Result>;
  submitSuccessHandler?: (result: GenericApiResponse<any>, values: AppFormikValues) => void;
  colErrorStyle?: string;
  customError?: AxiosError;
};

const AppFormik = <AppFormikValues extends FormikValues, Result extends GenericApiResponse<any>>(
  props: AppFormikConfig<AppFormikValues, Result>,
) => {
  const isMounted = useIsMounted();
  const { translateErrorMessage } = useTranslateErrorMessage();
  const [bannerErrors, setBannerErrors] = useState<string[]>([]);
  const {
    initialValues,
    initialErrors,
    children,
    displayErrorsOnTop = true,
    validate,
    validationSchema,
    validateOnChange,
    validateOnBlur,
    submitFormAPI,
    submitSuccessHandler,
    colErrorStyle,
    customError,
    ...rest
  } = props;

  const [showSpinner, setShowSpinner] = useState(false);

  const handleBannerErrorsString = (apiError: string) => {
    return translateErrorMessage(apiError);
  };

  useEffect(() => {
    if (customError)
      handleBannerErrors(customError);
    else
      setBannerErrors([]);
  }, [customError]);

  // Gestion de l'envoi du formulaire
  const submitHandler = async (values: AppFormikValues, actions: FormikHelpers<AppFormikValues>) => {
    if (submitFormAPI) {
      actions.setSubmitting(true);
      setShowSpinner(true);
      try {
        const result: GenericApiResponse<any> = await submitFormAPI(values);
        if (!isMounted()) return;
          actions.resetForm();
          if (submitSuccessHandler) {
            submitSuccessHandler(result, values);
          }
      } catch (error) {
        if (!isMounted()) return;
        if (error instanceof AxiosError) {
          handleBannerErrors(error);
        }
      } finally {
        if (isMounted()) {
          actions.setSubmitting(false);
          setShowSpinner(false);
        }
      }
    }
  };

  const handleBannerErrors = (error: AxiosError) => {
    // @ts-ignore
    const newBannerErrors = handleBannerErrorsString(error.response?.data.key || error.code || "defaultError");
    //actions.setErrors(formikErrors);
    setBannerErrors([newBannerErrors]);
  }

  return (
    <>
      {displayErrorsOnTop && bannerErrors && bannerErrors.length > 0 && (
        <>
          {bannerErrors.map((erreur, index) => (
            <AppRow key={"erreur_" + index}>
              <div className={colErrorStyle ? colErrorStyle : ""} style={{ width: "100%", marginBottom: "10px", marginTop: "10px", padding: 0 }}>
                <AppMessage messageType="ERROR" children={erreur} />
              </div>
            </AppRow>
          ))}
        </>
      )}
      <Formik
        initialValues={initialValues}
        initialErrors={initialErrors}
        validate={validate}
        validationSchema={validationSchema}
        validateOnChange={validateOnChange}
        validateOnBlur={validateOnBlur}
        onSubmit={submitHandler}
        onReset={() => setBannerErrors([])}
        {...rest}
      >
        {children}
      </Formik>
      {showSpinner && (
        <AppCol md={{ span: 2, offset: 5 }}>
          <AppSpinner className={styles.AppSpinnerFormik} animation="grow" />
        </AppCol>
      )}
    </>
  );
};

export default AppFormik;
